import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  CountryCode
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import SingleInput, {
  SingleInputRef
} from "@/components/controls/SingleInput";
import {
  hasPhoneNumber,
  isPhoneNumberValid
} from "@/components/controls/Form/helpers";
import { ValidationConfig } from "@/utils/validation";
import SingleSelect, {
  SingleSelectOption
} from "@/components/controls/SingleSelect";
import { useInputWithValidation } from "@/hooks/useInputWithValidation";
import { getCurrentLocale, getCountryFromCurrentLocale } from "@/utils/locale";
import {
  getLocalizedCountryOptions,
  NON_ISO_3166_1_CODES
} from "@/utils/countries";
import { cn } from "@/utils/utils";
import { CountryFlag } from "@/components/ui/country-flag";
import { getPlaceholder, FIELD_CLASSNAME, HALF_WRAPPER_CLASSNAME } from "../shared";
import { asString, ControlProps } from "../types";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";

const getCallingCodeOptions = (locale: string): SingleSelectOption[] =>
  getLocalizedCountryOptions(locale).map(({ code, label }) => ({
    id: code,
    displayName: label,
    suffix: `+${getCountryCallingCode(code)}`
  }));

const renderCountryFlag = (code: string | number) => (
  <CountryFlag code={String(code)} />
);

// The trigger shows only the flag, so the list is wider than it.
const CALLING_CODE_POPOVER_CLASSNAME = "w-64";

// Excludes the same codes the calling-code dropdown's options exclude (see
// countries.ts) — a country this control can resolve to (parsed from the
// value, or guessed) but can never show as a selection produces exactly that
// mismatch: SingleSelect can't find the "selected" value among its options
// and silently renders empty instead of the actual country.
const KNOWN_COUNTRIES = new Set<string>(
  getCountries().filter((code) => !NON_ISO_3166_1_CODES.has(code))
);

// All distinct calling codes libphonenumber-js knows about, longest first so a
// prefix match picks the most specific code (e.g. "1" vs a longer NANP code).
const KNOWN_CALLING_CODES = Array.from(
  new Set(getCountries().map((code) => getCountryCallingCode(code)))
).sort(
  (firstCallingCode, secondCallingCode) =>
    secondCallingCode.length - firstCallingCode.length
);

// Finds the calling code actually leading the stored value, independent of
// whatever country the dropdown currently shows — `country` can be a locale
// fallback that doesn't match what the user actually typed (e.g. unparseable
// input, or a value entered before this control existed). Returns null when
// nothing recognizable leads the string, so callers can leave it untouched
// rather than risk stripping (or duplicating) the wrong prefix.
export const getLeadingCallingCode = (value: string): string | null => {
  const match = value.match(/^\s*\+(\d{1,4})/);
  if (!match) return null;
  const digits = match[1];
  return (
    KNOWN_CALLING_CODES.find((code) => digits.startsWith(code)) ?? null
  );
};

// The country a calling code stands for when nothing narrows it down (US for
// +1, GB for +44): libphonenumber-js lists the main country first.
const getMainCountryForCallingCode = (
  callingCode: string
): CountryCode | undefined =>
  metadata.country_calling_codes[callingCode]?.find((code) =>
    KNOWN_COUNTRIES.has(code)
  );

// The dial-code country a value stands for (the parsed number's, else its
// leading calling code's), used to preselect the dropdown and to resync the
// flag after each commit. It never validates — see isPhoneNumberValid.
// `previousCountry` — the last explicitly-selected country, when known — is
// preferred over the locale default whenever the value still leads with that
// country's own calling code: an incomplete number (e.g. right after picking
// GB with nothing typed yet) can never be parsed into a country, and without
// this the resync would otherwise silently revert a deliberate selection.
export const guessCountry = (
  value: string,
  previousCountry?: CountryCode
): CountryCode => {
  try {
    const parsed = parsePhoneNumberFromString(value || "");
    // Only resolve to a country the dropdown can actually show as selected —
    // libphonenumber-js itself can still parse a value into an excluded code
    // (e.g. a real +383 Kosovo number parses to "XK"), which would otherwise
    // set a selection the dropdown's own (filtered) options can't match.
    if (parsed?.country && KNOWN_COUNTRIES.has(parsed.country)) {
      return parsed.country;
    }
  } catch {
    // Unparsable input is expected here — fall through to the calling code.
  }
  const leadingCallingCode = getLeadingCallingCode(value);
  if (
    previousCountry &&
    KNOWN_COUNTRIES.has(previousCountry) &&
    leadingCallingCode === getCountryCallingCode(previousCountry)
  ) {
    return previousCountry;
  }
  // A typed dial code the number is too short to parse ("+972") still names a
  // country — follow it rather than keep a flag that disagrees with it.
  const callingCodeCountry =
    leadingCallingCode && getMainCountryForCallingCode(leadingCallingCode);
  if (callingCodeCountry) return callingCodeCountry;
  // getCurrentLocale() can resolve to a numeric UN M49 region (e.g. es-419),
  // which getCountryFromCurrentLocale casts through unvalidated — guard it
  // here too, since getCountryCallingCode throws for anything libphonenumber
  // doesn't recognize as a country.
  const localeCountry = getCountryFromCurrentLocale();
  return KNOWN_COUNTRIES.has(localeCountry) ? localeCountry : "US";
};

// Pure computation behind handleCountrySelection's write, split out so it's
// directly testable without rendering the control or its SingleSelect: swaps
// only a leading dial code the same shape produces, leaving the rest of
// whatever the user typed untouched.
export const computeCountrySelectionValue = (
  inputValue: string,
  nextCountry: CountryCode
): string => {
  const callingCode = getCountryCallingCode(nextCountry);
  // Strip only a calling code we can actually identify in the current
  // value — never the whole leading digit run (a bare \d+ consumes the
  // national number too on an unspaced E.164 value like +14155550100),
  // and never the *selected* country's code when it doesn't match what's
  // actually there (the guessed `country` can be a locale fallback that
  // disagrees with the real value, which would otherwise concatenate two
  // codes, e.g. +49 +44...).
  const leadingCallingCode = getLeadingCallingCode(inputValue);
  const nationalPart = leadingCallingCode
    ? inputValue.replace(new RegExp(`^\\s*\\+${leadingCallingCode}\\s*`), "")
    : inputValue;
  // A bare "+" with nothing else typed is not a real national part — leaving
  // it in would save a malformed value like "+44 +".
  return nationalPart && nationalPart.trim() !== "+"
    ? `+${callingCode} ${nationalPart}`
    : `+${callingCode} `;
};

interface PhoneControlProps extends ControlProps {
  wrapperClassName?: string;
  // "inline" renders the record-row look (flush select + inline input) used by
  // the detail fields; the default is the boxed form look.
  variant?: "form" | "inline";
  placeholder?: string;
  disabled?: boolean;
  // Form look only: report every keystroke, not just blur, for hosts that
  // gate on the live value (a create form's submit button). The control then
  // skips its own format check, so the host must validate the value itself
  // (useFieldItem registers getFieldValidation for this).
  commitOnChange?: boolean;
  // For hosts that persist each save (record rows): a required phone is never
  // emptied, so an edit that leaves no number snaps back to the saved value
  // instead of showing blank. Create forms leave it unset — their own
  // required-field check owns presence there.
  required?: boolean;
}

// The saved value itself always passes: a number stored before format was
// checked must not trap the field on a click in and out with no edit.
const getPhoneValidation = (
  message: string,
  savedValue: string
): ValidationConfig => ({
  customRules: [
    {
      validate: (value: string) =>
        value === savedValue || isPhoneNumberValid(value),
      message
    }
  ]
});

// An invalid number, or one missing its dial code, never saves: the field
// shows the error until it is fixed (Escape restores the saved value).
export const PhoneControl: React.FC<PhoneControlProps> = (props) =>
  props.variant === "inline" ? (
    <InlinePhoneControl {...props} />
  ) : (
    <FormPhoneControl {...props} />
  );

const InlinePhoneControl: React.FC<PhoneControlProps> = ({
  value,
  onChange,
  name,
  wrapperClassName = "w-full",
  placeholder,
  disabled,
  required = false
}) => {
  const { t } = useTranslation();
  const locale = getCurrentLocale();
  const callingCodeOptions = useMemo(() => getCallingCodeOptions(locale), [locale]);
  const savedValue = asString(value);
  const phoneValidation = useMemo(
    () => getPhoneValidation(t("validation.invalidPhone"), savedValue),
    [t, savedValue]
  );
  // Bumped to remount the input on a snap-back, where the draft itself may
  // not change (it already holds the saved value).
  const [resetCount, setResetCount] = useState(0);
  // What the input shows. It runs ahead of savedValue when a dial code is
  // picked on an empty field: the host won't save a lone code, so the draft
  // carries it into the input for the number to be typed after it.
  const [draft, setDraft] = useState(savedValue);
  const [country, setCountry] = useState<CountryCode>(() =>
    guessCountry(savedValue)
  );

  useEffect(() => {
    setDraft(savedValue);
    setCountry((previousCountry) => guessCountry(savedValue, previousCountry));
  }, [savedValue]);

  // Set after a dial-code pick leaves a number invalid for the new country:
  // the input opens on it to be fixed rather than saving it. It opens once the
  // dropdown hands focus back, which would otherwise land on the flag and blur
  // the input straight away.
  const inputRef = useRef<SingleInputRef>(null);
  const editAfterPickRef = useRef(false);
  const handleDropdownCloseAutoFocus = (event: Event) => {
    if (!editAfterPickRef.current) return;
    editAfterPickRef.current = false;
    event.preventDefault();
    inputRef.current?.startEditing();
  };

  const handleCountrySelection = (selected: string | number) => {
    const nextCountry = String(selected) as CountryCode;
    const nextValue = computeCountrySelectionValue(draft, nextCountry);
    setCountry(nextCountry);
    setDraft(nextValue);
    if (isPhoneNumberValid(nextValue)) {
      onChange(nextValue);
    } else {
      editAfterPickRef.current = true;
    }
  };

  return (
    <div className={cn(wrapperClassName, "flex items-center gap-1 min-w-0")}>
      <div className="shrink-0">
        <SingleSelect
          fieldName={`${name}-country`}
          options={callingCodeOptions}
          selectedValue={country}
          onSelection={handleCountrySelection}
          renderIcon={renderCountryFlag}
          hideSelectedLabel
          popoverClassName={CALLING_CODE_POPOVER_CLASSNAME}
          showSearch
          variant="inline"
          disabled={disabled}
          onCloseAutoFocus={handleDropdownCloseAutoFocus}
        />
      </div>
      <div className="flex-1 min-w-0">
        <SingleInput
          ref={inputRef}
          // Remount when the draft changes so a dial-code pick shows in it, and
          // on a snap-back, where the draft may already equal the saved value.
          key={`${draft}:${resetCount}`}
          fieldName={name}
          initialValue={draft}
          validation={phoneValidation}
          // Escape drops an unsaved draft (a swap left invalid, a lone dial
          // code) along with the flag it set.
          onCancel={() => {
            setDraft(savedValue);
            setCountry((previousCountry) =>
              guessCountry(savedValue, previousCountry)
            );
          }}
          onSave={(newValue) => {
            // A required field with no saved number has nothing to snap back
            // to, so it keeps a lone dial code like picking a flag does.
            if (
              required &&
              !hasPhoneNumber(newValue) &&
              hasPhoneNumber(savedValue)
            ) {
              setDraft(savedValue);
              setResetCount((count) => count + 1);
              return;
            }
            setDraft(newValue);
            setCountry((previousCountry) =>
              guessCountry(newValue, previousCountry)
            );
            onChange(newValue);
          }}
          placeholder={placeholder ?? getPlaceholder(FieldDisplayType.PHONE, t)}
          variant="inline"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const FormPhoneControl: React.FC<PhoneControlProps> = ({
  value,
  onChange,
  name,
  wrapperClassName = HALF_WRAPPER_CLASSNAME,
  commitOnChange = false,
  required = false
}) => {
  const { t } = useTranslation();
  const locale = getCurrentLocale();
  const callingCodeOptions = useMemo(() => getCallingCodeOptions(locale), [locale]);
  const initialValue = asString(value);
  const errorId = useId();
  const [country, setCountry] = useState<CountryCode>(() =>
    guessCountry(initialValue)
  );

  // A response revision can keep this control mounted with a new value under
  // the same key (e.g. onboarding items keyed by item id) — resync the guessed
  // country whenever the underlying value changes out from under us.
  useEffect(() => {
    setCountry((previousCountry) => guessCountry(initialValue, previousCountry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const phoneValidation = useMemo(
    () => getPhoneValidation(t("validation.invalidPhone"), initialValue),
    [t, initialValue]
  );

  const {
    value: inputValue,
    error,
    setError,
    handleChange,
    handleBlur,
    setValue,
    validateInput
  } = useInputWithValidation({
    initialValue,
    // A host committing on change validates the live value itself (a create
    // form's field rule), so the control checks only for hosts saving on blur.
    validation: commitOnChange ? undefined : phoneValidation,
    onSave: (rawValue) => {
      if (required && !hasPhoneNumber(rawValue)) {
        setValue(initialValue);
        return;
      }
      setCountry((previousCountry) => guessCountry(rawValue, previousCountry));
      onChange(rawValue);
    }
  });

  // The hook resets the input to a new value from the host but not its error,
  // which would otherwise sit under a value it no longer describes.
  useEffect(() => {
    setError(null);
  }, [initialValue, setError]);

  // Picking a country is a deliberate edit, not a silent rewrite — see
  // computeCountrySelectionValue above for the write itself. Saves
  // immediately since there's no blur here, unless the number is now invalid
  // for the new country.
  const handleCountrySelection = (selected: string | number) => {
    const nextCountry = String(selected) as CountryCode;
    setCountry(nextCountry);
    const nextValue = computeCountrySelectionValue(inputValue, nextCountry);
    setValue(nextValue);
    if (validateInput(nextValue)) onChange(nextValue);
  };

  return (
    <div className={wrapperClassName}>
      <div className="flex gap-2">
        <div className="shrink-0">
          <SingleSelect
            fieldName={`${name}-country`}
            options={callingCodeOptions}
            selectedValue={country}
            onSelection={handleCountrySelection}
            renderIcon={renderCountryFlag}
            hideSelectedLabel
            popoverClassName={CALLING_CODE_POPOVER_CLASSNAME}
            showSearch
            variant="dropdown"
          />
        </div>
        <Input
          type="text"
          value={inputValue}
          onChange={(event) => {
            handleChange(event.target.value);
            if (commitOnChange) onChange(event.target.value);
          }}
          onBlur={handleBlur}
          placeholder={getPlaceholder(FieldDisplayType.PHONE, t)}
          className={cn("w-full", FIELD_CLASSNAME)}
          containerClassName="flex-1 min-w-0"
          data-test={`${name}-input`}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      {error && (
        <Text id={errorId} role="alert" variant="smBodyError">
          {error}
        </Text>
      )}
    </div>
  );
};
